import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Select } from '@ngxs/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';
import { UserState } from 'src/app/states/user-state/user.state';
import { AuthService } from '../auth.service';
import { RightType } from '../enums/rightType';
import { RightsService } from '../rights.service';
import { UnsavedChangesService } from '../unsavedChanges.service';

@Injectable()
export class AuthGuard implements CanActivate {

  @Select(UserState.getUserRights)
  public userRights$!: Observable<string[]>;
  
  public constructor(
    private router: Router,
    private authService: AuthService,
    private accessService: RightsService,
    private  unsavedChangesService: UnsavedChangesService) { }

 
  public canActivate(
    route: ActivatedRouteSnapshot,
     // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    state: RouterStateSnapshot): Observable<boolean> {
    if (!this.unsavedChangesService.shouldContinue()) {
      return of(false);
    }
    this.unsavedChangesService.resetComponent();

    const isInRole$ = this.userRights$
      .pipe(skipWhile(userRight => !userRight || userRight.length === 0), map(() => {
        if (route.routeConfig?.path === '**') {
          if (this.accessService.canSeeTasks()) {
            this.router.navigate(['/tasks']);
          }
          else if (this.accessService.canSeeCustomers()) {
            this.router.navigate(['/customers']);
          }
          else {
            this.router.navigate(['/plugins']);
          }
        }

        const rights = route.data.rights as Array<RightType>;
        return rights.length === 0 || rights.some(right => this.accessService.can(right)) ? true : false;
      }));

    const isAuthorized$ = this.authService.isDoneLoading$
      .pipe(map(isDoneLoading => {
        const isAuthorized = this.authService.isAuthorized();

        if (isDoneLoading && !isAuthorized) {
          this.authService.login();
        }

        return isDoneLoading && isAuthorized;
      }));

    return combineLatest([isAuthorized$, isInRole$])
      .pipe(map(([isAuthorized, isInRole]) => {
        if (isAuthorized && !isInRole) {
          this.router.navigate(['/unauthorized']);
        }

        return isAuthorized && isInRole;
      }));
  }
}
